import React from "react";

export const GRAPH_PLOT_MARGIN_LEFT = 20;
export const GRAPH_PLOT_MARGIN_RIGHT = 30;
export const GRAPH_Y_AXIS_WIDTH = 60;
export const LANE_ROW_INNER_GAP = 4;
export const LANE_ROW_MARGIN_BOTTOM = 8;
export const LANE_ROW_META_HEIGHT = 18;
export const LANE_ROW_TRACK_HEIGHT = 6;
export const ACTIVITY_BAND_PADDING_Y = 14;
export const GAP_BELOW_ACTIVITY = 10;
export const GRAPH_BRUSH_HEIGHT = 28;
export const STACKED_PLOT_HEIGHT = 250;
export const STACKED_BRUSH_ZONE_HEIGHT = GRAPH_BRUSH_HEIGHT + 12;

/** Altura de uma faixa (label + trilha + margem inferior). Deve bater com FieldAppHome.css. */
export const LANE_ROW_HEIGHT =
  LANE_ROW_META_HEIGHT +
  LANE_ROW_INNER_GAP +
  LANE_ROW_TRACK_HEIGHT +
  LANE_ROW_MARGIN_BOTTOM;

export function graphActivityBandHeight(laneCount) {
  if (!laneCount) return 0;
  const rowContentHeight =
    LANE_ROW_META_HEIGHT + LANE_ROW_INNER_GAP + LANE_ROW_TRACK_HEIGHT;
  return (
    ACTIVITY_BAND_PADDING_Y +
    laneCount * rowContentHeight +
    Math.max(0, laneCount - 1) * LANE_ROW_MARGIN_BOTTOM +
    GAP_BELOW_ACTIVITY
  );
}

function toMs(value) {
  if (value == null) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

const MACHINE_LANE_COLORS = [
  "#3498DB",
  "#9B59B6",
  "#E67E22",
  "#E74C3C",
  "#2ECC71",
  "#F39C12",
];

function clipIntervalToDomain(interval, domainStart, domainEnd) {
  const startMs = toMs(interval.start);
  const endMs = toMs(interval.end);
  if (startMs == null || endMs == null) return null;

  const visibleStart = Math.max(startMs, domainStart);
  const visibleEnd = Math.min(endMs, domainEnd);
  if (visibleEnd <= visibleStart) return null;

  return {
    ...interval,
    start: new Date(visibleStart).toISOString(),
    end: new Date(visibleEnd).toISOString(),
  };
}

function clipIntervalsToDomain(intervals, domainStart, domainEnd) {
  return (intervals || [])
    .map((interval) => clipIntervalToDomain(interval, domainStart, domainEnd))
    .filter(Boolean);
}

function intersectIntervals(first, second) {
  const startMs = Math.max(toMs(first.start), toMs(second.start));
  const endMs = Math.min(toMs(first.end), toMs(second.end));
  if (startMs == null || endMs == null || endMs <= startMs) return null;
  return {
    start: new Date(startMs).toISOString(),
    end: new Date(endMs).toISOString(),
    machineIdent: first.machineIdent || second.machineIdent,
    endEvent: first.endEvent,
    sessionDetail: first.sessionDetail,
    sessionUuid: first.sessionUuid,
    source: first.source || second.source,
  };
}

const TELEMETRY_BURST_GAP_MS = 20 * 60 * 1000;

function buildIntervalsFromGraphBursts(graphData, domainStart, domainEnd, gapMs) {
  if (!graphData || !graphData.length) return [];

  const sorted = graphData
    .filter((point) => point.time != null && !Number.isNaN(point.time))
    .sort((a, b) => a.time - b.time);

  if (!sorted.length) return [];

  const burstGap = gapMs || TELEMETRY_BURST_GAP_MS;
  const rawIntervals = [];
  let burstStart = sorted[0];
  let burstEnd = sorted[0];

  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1];
    const current = sorted[index];
    if (current.time - previous.time > burstGap) {
      rawIntervals.push({
        start: new Date(burstStart.time).toISOString(),
        end: new Date(burstEnd.time).toISOString(),
      });
      burstStart = current;
    }
    burstEnd = current;
  }

  rawIntervals.push({
    start: new Date(burstStart.time).toISOString(),
    end: new Date(burstEnd.time).toISOString(),
  });

  return clipIntervalsToDomain(rawIntervals, domainStart, domainEnd);
}

function buildTelemetryLane(telemetryApi, domainStart, domainEnd) {
  const intervals = clipIntervalsToDomain(
    (telemetryApi && telemetryApi.intervals) || [],
    domainStart,
    domainEnd
  );
  if (!intervals.length) return null;

  return {
    id: (telemetryApi && telemetryApi.id) || "telemetry",
    type: "telemetry",
    label: "Telemetria",
    color: (telemetryApi && telemetryApi.color) || "#1ABB9C",
    intervals,
  };
}

function attachSessionDetailFromSource(interval, sourceIntervals) {
  if (interval.sessionDetail || !sourceIntervals || !sourceIntervals.length) {
    return interval;
  }

  const visibleStart = toMs(interval.start);
  const visibleEnd = toMs(interval.end);
  if (visibleStart == null || visibleEnd == null) return interval;

  let bestMatch = null;
  let bestOverlap = 0;

  sourceIntervals.forEach((source) => {
    if (!source.sessionDetail) return;
    const sourceStart = toMs(source.start);
    const sourceEnd = toMs(source.end);
    if (sourceStart == null || sourceEnd == null) return;

    const overlapStart = Math.max(visibleStart, sourceStart);
    const overlapEnd = Math.min(visibleEnd, sourceEnd);
    if (overlapEnd <= overlapStart) return;

    const overlap = overlapEnd - overlapStart;
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestMatch = source;
    }
  });

  if (!bestMatch) return interval;

  return {
    ...interval,
    sessionDetail: bestMatch.sessionDetail,
    sessionUuid: interval.sessionUuid || bestMatch.sessionUuid,
    endEvent: interval.endEvent || bestMatch.endEvent,
  };
}

function buildMachineLanes(
  tracking,
  machineApiLanes,
  domainStart,
  domainEnd,
  options = {}
) {
  const { strictGpsIntersection = false, intersectWithGps = false } = options;
  const machines = (tracking && tracking.machines) || [];
  const gpsByIdent = {};
  machines.forEach((machine) => {
    gpsByIdent[machine.ident] = machine.gpsIntervals || [];
  });

  const laneByIdent = new Map();

  (machineApiLanes || []).forEach((lane, index) => {
    if (!lane.label) return;

    const sourceIntervals = lane.intervals || [];

    let intervals = clipIntervalsToDomain(
      sourceIntervals,
      domainStart,
      domainEnd
    );
    if (!intervals.length) return;

    const gpsEver = (gpsByIdent[lane.label] || []).length > 0;
    const gpsClipped = clipIntervalsToDomain(
      gpsByIdent[lane.label] || [],
      domainStart,
      domainEnd
    );

    if ((intersectWithGps || strictGpsIntersection) && gpsEver) {
      const filtered = [];
      intervals.forEach((syncInterval) => {
        gpsClipped.forEach((gpsInterval) => {
          const hit = intersectIntervals(syncInterval, gpsInterval);
          if (hit) {
            filtered.push({
              ...hit,
              machineIdent: lane.label,
            });
          }
        });
      });
      if (filtered.length) {
        intervals = filtered;
      } else if (strictGpsIntersection) {
        return;
      }
    }

    intervals = intervals.map((interval) =>
      attachSessionDetailFromSource(interval, sourceIntervals)
    );

    const existing = laneByIdent.get(lane.label);
    if (existing) {
      existing.intervals = existing.intervals.concat(intervals);
      return;
    }

    laneByIdent.set(lane.label, {
      id: lane.id || `machine-${lane.label}`,
      type: "machine",
      label: lane.label,
      color:
        lane.color ||
        MACHINE_LANE_COLORS[index % MACHINE_LANE_COLORS.length],
      intervals: intervals.slice(),
    });
  });

  return Array.from(laneByIdent.values());
}

function domainFromGraph(graphData) {
  if (!graphData || graphData.length < 1) return null;
  const domainStart = graphData[0].time;
  const domainEnd = graphData[graphData.length - 1].time;
  if (domainStart == null || domainEnd == null) return null;
  return { domainStart, domainEnd };
}

/**
 * Faixas de máquina alinhadas aos pontos visíveis no gráfico (ex.: monitoramento
 * automático em Operações, onde graphSeries = GPS da máquina aberta).
 */
export function buildMachineLanesMatchingGraph(
  tracking,
  machineApiLanes,
  graphData
) {
  const domain = domainFromGraph(graphData);
  if (!domain || !graphData.length) return [];

  const { domainStart, domainEnd } = domain;
  const lanes = (machineApiLanes || []).filter((lane) => lane && lane.label);
  if (!lanes.length) return [];

  const graphGpsByIdent = {};
  lanes.forEach((lane) => {
    graphGpsByIdent[lane.label] = buildIntervalsFromGraphBursts(
      graphData,
      domainStart,
      domainEnd
    );
  });

  const baseMachines = (tracking && tracking.machines) || [];
  const machinesByIdent = new Map(
    baseMachines.map((machine) => [machine.ident, { ...machine }])
  );

  lanes.forEach((lane) => {
    const existing = machinesByIdent.get(lane.label) || { ident: lane.label };
    machinesByIdent.set(lane.label, {
      ...existing,
      gpsIntervals: graphGpsByIdent[lane.label] || [],
    });
  });

  const syncLanes = buildMachineLanes(
    { ...(tracking || {}), machines: Array.from(machinesByIdent.values()) },
    lanes,
    domainStart,
    domainEnd,
    { strictGpsIntersection: true }
  );

  if (syncLanes.length) {
    return syncLanes;
  }

  return lanes
    .map((lane, index) => {
      const bursts = graphGpsByIdent[lane.label] || [];
      if (!bursts.length) return null;

      const intervals = bursts.map((interval) =>
        attachSessionDetailFromSource(interval, lane.intervals || [])
      );

      return {
        id: lane.id || `machine-${lane.label}`,
        type: "machine",
        label: lane.label,
        color:
          lane.color ||
          MACHINE_LANE_COLORS[index % MACHINE_LANE_COLORS.length],
        intervals,
      };
    })
    .filter(Boolean);
}

export function machinesActiveInGraph(tracking, graphData) {
  const domain = domainFromGraph(graphData);
  if (!domain) return [];

  const machineApiLanes = ((tracking && tracking.activityLanes) || []).filter(
    (lane) => lane.type === "machine"
  );
  return buildMachineLanes(
    tracking,
    machineApiLanes,
    domain.domainStart,
    domain.domainEnd
  ).map((lane) => ({ ident: lane.label }));
}

const DAY_SPLIT_BRIDGE_MS = 3 * 60 * 1000;

function clockFromIso(value) {
  const match = String(value || "").match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

function looksLikeDaySplit(last, current) {
  const gap = current.startMs - last.endMs;
  if (gap < 0 || gap > DAY_SPLIT_BRIDGE_MS) return false;
  const endClock = clockFromIso(last.end);
  const startClock = clockFromIso(current.start);
  if (!endClock || !startClock) return false;
  return (
    endClock.hours === 23 &&
    endClock.minutes >= 59 &&
    startClock.hours === 0 &&
    startClock.minutes === 0
  );
}

function clipFlagsKnown(interval) {
  return (
    typeof interval.startClipped === "boolean" ||
    typeof interval.endClipped === "boolean"
  );
}

function coalesceIntervals(intervals) {
  const sorted = (intervals || [])
    .map((interval) => ({
      ...interval,
      startMs: toMs(interval.start),
      endMs: toMs(interval.end),
    }))
    .filter(
      (interval) =>
        interval.startMs != null &&
        interval.endMs != null &&
        interval.endMs > interval.startMs
    )
    .sort((left, right) => left.startMs - right.startMs);

  if (!sorted.length) return [];

  const merged = [sorted[0]];
  for (let index = 1; index < sorted.length; index += 1) {
    const current = sorted[index];
    const last = merged[merged.length - 1];
    const gap = current.startMs - last.endMs;
    const flagsKnown = clipFlagsKnown(last) || clipFlagsKnown(current);
    const clippedBridge =
      gap >= 0 &&
      gap <= DAY_SPLIT_BRIDGE_MS &&
      (Boolean(last.endClipped) || Boolean(current.startClipped));
    const touches =
      gap <= 0 ||
      clippedBridge ||
      (!flagsKnown && looksLikeDaySplit(last, current));
    if (!touches) {
      merged.push(current);
      continue;
    }
    if (current.endMs >= last.endMs) {
      last.endMs = current.endMs;
      last.end = current.end;
      last.endClipped = Boolean(current.endClipped);
      if (!current.endClipped) {
        last.endEvent = current.endEvent || last.endEvent;
      }
    }
  }

  return merged.map(({ startMs, endMs, ...interval }) => interval);
}

function mergedTelemetryLane(apiLanes) {
  const telemetryLanes = (apiLanes || []).filter(
    (lane) => lane && lane.type === "telemetry"
  );
  if (!telemetryLanes.length) return null;

  const intervals = [];
  telemetryLanes.forEach((lane) => {
    (lane.intervals || []).forEach((interval) => intervals.push(interval));
  });

  return {
    id: "telemetry",
    type: "telemetry",
    label: "Telemetria",
    color: telemetryLanes[0].color || "#1ABB9C",
    intervals: coalesceIntervals(intervals),
  };
}

export function telemetryBoundaryMarkers(activityLanes) {
  const telemetry = mergedTelemetryLane(activityLanes);
  if (!telemetry) return [];

  const markers = [];
  telemetry.intervals.forEach((interval) => {
    const startMs = toMs(interval.start);
    const endMs = toMs(interval.end);
    if (startMs != null && !interval.startClipped) {
      markers.push({ timeMs: startMs, kind: "start" });
    }
    if (
      endMs != null &&
      endMs !== startMs &&
      !interval.endClipped &&
      interval.endEvent === "TELEMETRIA_FIM"
    ) {
      markers.push({ timeMs: endMs, kind: "end" });
    }
  });
  return markers;
}

export function buildDisplayActivityLanes(tracking, graphData) {
  if (!tracking) return [];

  const apiLanes = tracking.activityLanes || [];
  const domain = domainFromGraph(graphData);
  if (!domain) return [];

  const { domainStart, domainEnd } = domain;
  const telemetryApi = mergedTelemetryLane(apiLanes);
  const machineApiLanes = apiLanes.filter((lane) => lane.type === "machine");
  const telemetryLane = buildTelemetryLane(
    telemetryApi,
    domainStart,
    domainEnd
  );

  const lanes = [];
  if (telemetryLane) {
    lanes.push(telemetryLane);
  }

  buildMachineLanes(tracking, machineApiLanes, domainStart, domainEnd).forEach(
    (lane) => lanes.push(lane)
  );

  return lanes;
}

/** Operação sem ASSUMIR: barra do início ao fim do gráfico, não bursts de GPS. */
export function withOperationSessionFallback(lanes, graphData, machineIdent) {
  const hasMachine = (lanes || []).some(
    (lane) => lane.type === "machine" && lane.intervals && lane.intervals.length
  );
  if (hasMachine || !machineIdent || !graphData || graphData.length < 2) {
    return lanes || [];
  }

  return (lanes || [])
    .filter((lane) => lane.type !== "machine")
    .concat([
      {
        id: `machine-${machineIdent}`,
        type: "machine",
        label: machineIdent,
        color: MACHINE_LANE_COLORS[0],
        intervals: [
          {
            start: new Date(graphData[0].time).toISOString(),
            end: new Date(graphData[graphData.length - 1].time).toISOString(),
          },
        ],
      },
    ]);
}

export function sumVisibleDurationMs(intervals, domainStart, domainEnd) {
  let total = 0;
  (intervals || []).forEach((interval) => {
    const startMs = toMs(interval.start);
    const endMs = toMs(interval.end);
    if (startMs == null || endMs == null) return;

    const visibleStart = Math.max(startMs, domainStart);
    const visibleEnd = Math.min(endMs, domainEnd);
    if (visibleEnd > visibleStart) {
      total += visibleEnd - visibleStart;
    }
  });
  return total;
}

export function formatActivityDuration(ms) {
  if (!ms || ms <= 0) return "0 min";
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}min`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes} min`;
}

function laneDisplayLabel(lane) {
  if (lane.type === "telemetry") {
    return "Telemetria";
  }
  return lane.label || "Máquina";
}

export function GraphActivityLaneRow({
  lane,
  domainStart,
  domainEnd,
  onActivityIntervalClick,
}) {
  if (domainStart == null || domainEnd == null || domainEnd <= domainStart) {
    return null;
  }

  const span = domainEnd - domainStart;
  const totalMs = sumVisibleDurationMs(lane.intervals, domainStart, domainEnd);
  const label = laneDisplayLabel(lane);
  const hasSegments = (lane.intervals || []).some((interval) => {
    const startMs = toMs(interval.start);
    const endMs = toMs(interval.end);
    if (startMs == null || endMs == null) return false;
    const visibleStart = Math.max(startMs, domainStart);
    const visibleEnd = Math.min(endMs, domainEnd);
    return visibleEnd > visibleStart;
  });

  return (
    <div className="field-app-graph-activity-row">
      <div className="field-app-graph-activity-row-meta">
        <span className="field-app-graph-activity-row-label" title={label}>
          {label}
        </span>
        <span className="field-app-graph-activity-row-duration">
          {formatActivityDuration(totalMs)}
        </span>
      </div>
      <div
        className={
          hasSegments
            ? "field-app-graph-activity-row-track"
            : "field-app-graph-activity-row-track field-app-graph-activity-row-track--empty"
        }
      >
        {(lane.intervals || []).map((interval, index) => {
          const startMs = toMs(interval.start);
          const endMs = toMs(interval.end);
          if (startMs == null || endMs == null) return null;

          const visibleStart = Math.max(startMs, domainStart);
          const visibleEnd = Math.min(endMs, domainEnd);
          if (visibleEnd <= visibleStart) return null;

          const leftPct = ((visibleStart - domainStart) / span) * 100;
          const widthPct = ((visibleEnd - visibleStart) / span) * 100;

          return (
            <button
              key={`${lane.id}-${index}`}
              type="button"
              className="field-app-graph-activity-bar"
              style={{
                left: `${leftPct}%`,
                width: `${Math.max(widthPct, 0.5)}%`,
                backgroundColor: lane.color || "#1ABB9C",
              }}
              title={`${label}: ${formatActivityDuration(
                visibleEnd - visibleStart
              )}`}
              onClick={
                onActivityIntervalClick
                  ? () =>
                      onActivityIntervalClick({
                        lane,
                        interval,
                        startMs: visibleStart,
                        endMs: visibleEnd,
                      })
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export default function GraphActivityLanesBand({
  activityLanes,
  visibleStartTime,
  visibleEndTime,
  onActivityIntervalClick,
}) {
  if (!activityLanes || !activityLanes.length) {
    return null;
  }

  return (
    <div className="field-app-graph-activity-band">
      {activityLanes.map((lane) => (
        <GraphActivityLaneRow
          key={lane.id}
          lane={lane}
          domainStart={visibleStartTime}
          domainEnd={visibleEndTime}
          onActivityIntervalClick={onActivityIntervalClick}
        />
      ))}
    </div>
  );
}

export function stackedGraphHeight(laneCount) {
  if (!laneCount) return 300;
  return (
    STACKED_PLOT_HEIGHT +
    graphActivityBandHeight(laneCount) +
    STACKED_BRUSH_ZONE_HEIGHT
  );
}
